type TiCalculatorConfig = {
  className: 'TI84PCE'
  cssFileName: 'ti84pce-min.css'
  scriptFileNames: ['ELG-min.js']
  settings: Record<string, any>
}

export type TiCalculatorInstance = {
  errorHandler?: (level: unknown, message: string) => void
  killInstance?: () => void
}

declare global {
  interface Window {
    TI84PCE?: new (
      settings: Record<string, any>,
      timeout?: number
    ) => TiCalculatorInstance
    TI_ERROR_LEVEL?: {
      SEVERE?: unknown
    }
  }
}

const TI_ASSET_BASE = `${process.env.PUBLIC_URL}/TI-Calculators`
const SHARED_STANDALONE_STYLESHEET = 'standalone-example-styles-min.css'
const scriptLoaders = new Map<string, Promise<void>>()

type TiErrorLevel = {
  SEVERE?: unknown
}

const TI84_CALCULATOR_CONFIG: TiCalculatorConfig = {
  className: 'TI84PCE',
  cssFileName: 'ti84pce-min.css',
  scriptFileNames: ['ELG-min.js'],
  settings: {
    ROMLocation: 'bin/No_AppsCE.h84statej',
    FaceplateLocation: 'images/TI84CE_touch.svg',
    KeyMappingFile: '',
    KeyHistBufferLength: '10',
    DisplayMode: 'CLASSIC',
    AngleMode: 'DEG',
    setTabOrder: 0,
    setScreenReaderAria: true,
    setAccessibleDisplay: true
  }
}

/**
 * TI calculators expect their vendor stylesheet to load unchanged because it
 * contains vendor-specific selectors and relative asset urls.
 */
const ensureStylesheet = (href: string, styleId: string) => {
  const existingLink = document.querySelector(
    `link[data-ti-style-id="${styleId}"]`
  )

  if (existingLink) {
    return
  }

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  link.dataset.tiStyleId = styleId
  document.head.appendChild(link)
}

/**
 * TI emulator scripts register global constructor functions on window and need
 * to execute as classic browser scripts instead of React module imports.
 */
const ensureScript = (src: string, scriptId: string) => {
  const existingScript = document.querySelector(
    `script[data-ti-script-id="${scriptId}"]`
  ) as HTMLScriptElement | null

  if (existingScript?.dataset.loaded === 'true') {
    return Promise.resolve()
  }

  const cachedLoader = scriptLoaders.get(src)

  if (cachedLoader) {
    return cachedLoader
  }

  const scriptLoader = new Promise<void>((resolve, reject) => {
    const script = existingScript || document.createElement('script')

    script.src = src
    script.async = true
    script.dataset.tiScriptId = scriptId

    script.onload = () => {
      script.dataset.loaded = 'true'
      resolve()
    }

    script.onerror = () => {
      scriptLoaders.delete(src)
      reject(new Error(`Failed to load TI calculator script: ${src}`))
    }

    if (!existingScript) {
      document.body.appendChild(script)
    }
  })

  scriptLoaders.set(src, scriptLoader)

  return scriptLoader
}

/**
 * The TI vendor scripts expose some globals as lexical script bindings instead
 * of window properties, so React needs a lookup that can read either form.
 */
const getVendorGlobalValue = <TValue>(globalName: string) => {
  const windowValue = (window as unknown as Record<string, TValue | undefined>)[
    globalName
  ]

  if (windowValue) {
    return windowValue
  }

  try {
    return window.eval(globalName) as TValue
  } catch {
    return undefined
  }
}

/**
 * This mounts the TI84 emulator into the provided element id by reusing the
 * exact vendor settings from the example app while letting React own the host
 * node.
 */
export const showTiCalculator = async (elementId: string) => {
  ensureStylesheet(
    `${TI_ASSET_BASE}/css/${SHARED_STANDALONE_STYLESHEET}`,
    'ti-shared-styles'
  )
  ensureStylesheet(
    `${TI_ASSET_BASE}/css/${TI84_CALCULATOR_CONFIG.cssFileName}`,
    'ti84-styles'
  )

  await Promise.all(
    TI84_CALCULATOR_CONFIG.scriptFileNames.map((scriptFileName) => {
      return ensureScript(
        `${TI_ASSET_BASE}/js/${scriptFileName}`,
        scriptFileName
      )
    })
  )

  const CalculatorConstructor = getVendorGlobalValue<
    new (
      settings: Record<string, any>,
      timeout?: number
    ) => TiCalculatorInstance
  >(TI84_CALCULATOR_CONFIG.className)

  if (!CalculatorConstructor) {
    throw new Error(
      `TI calculator constructor is not available for ${TI84_CALCULATOR_CONFIG.className}`
    )
  }

  const calculatorInstance = new CalculatorConstructor(
    {
      ...TI84_CALCULATOR_CONFIG.settings,
      elementId,
      ROMLocation: `${TI_ASSET_BASE}/${TI84_CALCULATOR_CONFIG.settings.ROMLocation}`,
      FaceplateLocation: `${TI_ASSET_BASE}/${TI84_CALCULATOR_CONFIG.settings.FaceplateLocation}`
    },
    60000
  )

  /**
   * The sample app surfaces severe vendor errors immediately.
   * Keeping the same behavior makes debugging integration issues faster.
   */
  calculatorInstance.errorHandler = (level, message) => {
    const errorLevel = getVendorGlobalValue<TiErrorLevel>('TI_ERROR_LEVEL')

    if (level === errorLevel?.SEVERE) {
      alert(message)
    }

    console.log(message)
  }

  return calculatorInstance
}

/**
 * The TI runtime owns DOM inside the mount node, so teardown clears both the
 * vendor instance and any generated markup while keeping the React mount node
 * in place for the next calculator boot.
 */
export const destroyTiCalculator = (
  calculatorInstance: TiCalculatorInstance | null,
  calculatorElement: HTMLDivElement | null
) => {
  calculatorInstance?.killInstance?.()

  if (!calculatorElement) {
    return
  }

  const calculatorMountElement = calculatorElement.querySelector(
    '#my_calculator'
  ) as HTMLDivElement | null

  if (!calculatorMountElement) {
    return
  }

  calculatorMountElement.innerHTML = ''
}
